import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import "./status.css";

const BROGVERSE_LOGO_FALLBACK = "/favicon.ico";
const STATUS_POLL_INTERVAL_MS = 5000;
const STATUS_OFFLINE_FALLBACK_MS = 20000;
const STATUS_REQUEST_TIMEOUT_MS = 4500;

const normalizeFavicon = (favicon) => {
  if (!favicon || typeof favicon !== "string") {
    return BROGVERSE_LOGO_FALLBACK;
  }

  const cleaned = favicon.trim();

  // API may return raw base64 without the data URI prefix.
  if (cleaned && !cleaned.startsWith("http") && !cleaned.startsWith("data:image")) {
    return `data:image/png;base64,${cleaned}`;
  }

  return cleaned || BROGVERSE_LOGO_FALLBACK;
};

const stripMcFormatting = (text) => {
  if (!text) {
    return "No MOTD provided.";
  }

  return String(text)
    .replace(/\u00A7[0-9A-FK-OR]/gi, "")
    .replace(/§[0-9A-FK-OR]/gi, "")
    .trim();
};

const parseMotd = (description) => {
  if (typeof description === "string") {
    return stripMcFormatting(description);
  }

  if (Array.isArray(description)) {
    return stripMcFormatting(description.join(" "));
  }

  if (description && typeof description === "object") {
    if (Array.isArray(description.text)) {
      return stripMcFormatting(description.text.join(" "));
    }

    return stripMcFormatting(description.text || description.extra?.map((part) => part?.text).join(" "));
  }

  return "No MOTD provided.";
};

const Status = () => {
  const [serverData, setServerData] = useState({
    motd: "Loading server status...",
    playersOnline: 0,
    playersMax: 0,
    latency: null,
    favicon: BROGVERSE_LOGO_FALLBACK,
    error: false,
  });
  const [iconSrc, setIconSrc] = useState(BROGVERSE_LOGO_FALLBACK);
  const firstLoadStartedAtRef = useRef(Date.now());
  const lastResponseAtRef = useRef(null);
  const requestInFlightRef = useRef(false);

  const setOfflineState = useCallback(() => {
    setServerData((prev) => {
      if (prev.error && prev.motd === "Server Offline") {
        return prev;
      }

      return {
        motd: "Server Offline",
        playersOnline: 0,
        playersMax: 0,
        latency: null,
        favicon: BROGVERSE_LOGO_FALLBACK,
        error: true,
      };
    });
  }, []);

  const applyNoResponseFallback = useCallback(() => {
    const now = Date.now();
    const referenceTime = lastResponseAtRef.current ?? firstLoadStartedAtRef.current;

    if (now - referenceTime >= STATUS_OFFLINE_FALLBACK_MS) {
      setOfflineState();
    }
  }, [setOfflineState]);

  const fetchServerData = useCallback(async () => {
    if (requestInFlightRef.current) {
      return;
    }

    requestInFlightRef.current = true;
    const url = "https://api.minetools.eu/ping/mc.brogverse.cc";

    try {
      const response = await axios.get(url, { timeout: STATUS_REQUEST_TIMEOUT_MS });
      const data = response.data;
      lastResponseAtRef.current = Date.now();

      if (data.error) {
        setOfflineState();
      } else {
        setServerData({
          motd: parseMotd(data.description),
          playersOnline: data.players?.online ?? 0,
          playersMax: data.players?.max ?? 0,
          latency: data.latency ?? null,
          favicon: normalizeFavicon(data.favicon),
          error: false,
        });
      }
    } catch (error) {
      applyNoResponseFallback();
    } finally {
      requestInFlightRef.current = false;
    }
  }, [applyNoResponseFallback, setOfflineState]);

  useEffect(() => {
    firstLoadStartedAtRef.current = Date.now();
    fetchServerData();

    const pollId = setInterval(() => {
      fetchServerData();
      applyNoResponseFallback();
    }, STATUS_POLL_INTERVAL_MS);

    return () => clearInterval(pollId);
  }, [fetchServerData, applyNoResponseFallback]);

  useEffect(() => {
    setIconSrc(serverData.favicon || BROGVERSE_LOGO_FALLBACK);
  }, [serverData.favicon]);

  const handleIconError = () => {
    if (iconSrc !== BROGVERSE_LOGO_FALLBACK) {
      setIconSrc(BROGVERSE_LOGO_FALLBACK);
    }
  };

  return (
    <section className="server-status-card" aria-live="polite">
      <div className="server-status-image-wrap">
        <img
          src={iconSrc}
          alt="Minecraft server icon"
          className="server-status-image"
          onError={handleIconError}
        />
      </div>
      <div className="server-status-content">
        <div className="server-status-top-row">
          <h2>mc.brogverse.cc</h2>
          <span className={`server-chip ${serverData.error ? "offline" : "online"}`}>
            {serverData.error ? "Offline" : "Online"}
          </span>
        </div>

        <p className="server-motd">{serverData.motd}</p>

        <div className="server-stats-row">
          <span>
            <strong>Players:</strong> {serverData.playersOnline}/{serverData.playersMax}
          </span>
          <span>
            <strong>Latency:</strong> {serverData.latency ? `${Math.round(serverData.latency)}ms` : "-"}
          </span>
        </div>
      </div>
    </section>
  );
};

export default Status;
